class RoProductMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;

    this.galleryImages = this.querySelector('#gallery-images');
    this.images = this.galleryImages.querySelectorAll('.gallery-image');
    this.imageCount = this.images.length;
    this.prevBtn = this.querySelector('#prev-btn');
    this.nextBtn = this.querySelector('#next-btn');
    
    this.dots = this.querySelectorAll('.navigation-dots .dot');

    this.zoomOverlay = this.querySelector('#zoom-overlay');
    this.zoomImagesContainer = this.querySelector('#zoom-images-container');
    this.zoomImages = Array.from(this.zoomImagesContainer.children);
    this.zoomClose = this.querySelector('#zoom-close');
    this.thumbImages = this.querySelectorAll('#thumbnails-bar img');

    this.documentBody = document.querySelector('body');
    this.lastFocusedElement = null;

    this.isMobile = window.matchMedia('(max-width: 1024px)').matches;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.addEventListeners();
    this.updateButtons();
  }

  addEventListeners() {
    // WINDOW
    window.addEventListener('resize', () => {
      this.isMobile = window.matchMedia('(max-width: 1024px)').matches;
      this.updateButtons();
    });

    // BUTTON EVENTS
    this.prevBtn.addEventListener('click', this.handleClick.bind(this));
    this.nextBtn.addEventListener('click', this.handleClick.bind(this));

    ['mousemove'].forEach(eventType => {
      this.nextBtn.addEventListener(eventType, this.handleMouseEvents.bind(this));
      this.prevBtn.addEventListener(eventType, this.handleMouseEvents.bind(this));
    });

    // GALLERY EVENTS
    this.galleryImages.addEventListener('mousemove', this.handleMouseEvents.bind(this));
    this.galleryImages.addEventListener('mouseleave', this.handleMouseEvents.bind(this));

    this.galleryImages.addEventListener('scroll', () => {
      const scrollPosition = this.galleryImages.scrollLeft;
      const newCurrentIndex = Math.round(scrollPosition / this.images[0].clientWidth);

      if (newCurrentIndex !== this.currentIndex) {
        this.currentIndex = Math.min(Math.max(newCurrentIndex, 0), this.imageCount - 1);
      }
      this.updateUi();
    });

    // DOT EVENTS
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.scrollAmount = index * this.images[0].clientWidth;
        this.scrollGallery();
        this.updateUi();
      });
    });

    // ZOOM EVENTS
    this.images.forEach((image) => {
      if (image.id !== 'gallery-spin-image') {

        const index = parseInt(image.dataset.galleryIndex, 10);

        image.addEventListener('click', (event) => {
          this.openZoom(image, index, event);
        });

        image.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.openZoom(image, index, event);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.navigateMainGallery(-1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.navigateMainGallery(1);
          }
        });
      }
    });

    this.zoomImagesContainer.addEventListener('scroll', this.handleZoomScroll.bind(this));
    this.zoomClose.addEventListener('click', this.closeZoom.bind(this));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.zoomOverlay.classList.contains('tw-hidden')) {
        this.closeZoom();
      }
      if (
        !this.zoomOverlay.classList.contains('tw-hidden') &&
        !event.target.closest('#thumbnails-bar')
      ) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.navigateZoom(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigateZoom(1);
        }
      }
    });

    this.thumbImages.forEach((thumb) => {
      thumb.addEventListener('click', this.handleThumbsClick.bind(this));
      thumb.addEventListener('keydown', this.handleThumbKeydown.bind(this));
    });

    this.hideMagic360TrialLink();
  }

  hideMagic360TrialLink() {
    const hide = () => {
      const link = document.querySelector('a[href="https://www.magictoolbox.com/magic360/"]');
      if (link) {
        link.style.setProperty('display', 'none', 'important');
        return true;
      }
      return false;
    };

    if (hide()) return;

    const observer = new MutationObserver(() => {
      if (hide()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  updateUi() {
    this.updateDots();
    this.updateButtons();
  }

  scrollGallery() {
    this.galleryImages.scrollTo({
      left: this.scrollAmount,
      behavior: 'smooth'
    });
  }

  handleClick(event) {
    if (event.currentTarget.name === 'next') {
      this.scrollAmount = this.galleryImages.scrollLeft + this.images[0].clientWidth;
    }
    else if (event.currentTarget.name === 'previous') {
      this.scrollAmount = this.galleryImages.scrollLeft - this.images[0].clientWidth;
    }

    this.scrollGallery();
  }

  navigateMainGallery(direction) {
    this.scrollAmount = this.galleryImages.scrollLeft + (direction * this.images[0]?.clientWidth || 0);
    this.scrollAmount = Math.max(0, Math.min(this.scrollAmount, this.galleryImages.scrollWidth - this.galleryImages.clientWidth));
    this.scrollGallery();
    this.updateUi();
  }

  navigateZoom(direction) {
    const thumbCount = this.thumbImages.length;
    if (thumbCount === 0) return;

    const currentIndex = Array.from(this.thumbImages).findIndex(
      (thumb) => thumb.classList.contains('tw-thumbnail-active')
    );
    const fallbackIndex = currentIndex >= 0 ? currentIndex : 0;
    const newIndex = Math.max(0, Math.min(fallbackIndex + direction, thumbCount - 1));

    const targetThumb = this.thumbImages[newIndex];
    const zoomIndex = parseInt(targetThumb.dataset.thumbIndex, 10);
    const zoomedMedia = this.querySelector(
      `#zoom-images-container [data-zoom-index="${zoomIndex}"]`
    );
    if (zoomedMedia) {
      zoomedMedia.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.setActiveThumbnail(zoomIndex);
      targetThumb.focus();
    }
  }

  handleThumbKeydown(event) {
    const thumbIndex = parseInt(event.currentTarget.dataset.thumbIndex, 10);
    const thumbCount = this.thumbImages.length;
    let handled = false;

    if (this.isMobile) {
      if (event.key === 'ArrowLeft' && thumbIndex > 0) {
        event.preventDefault();
        this.thumbImages[thumbIndex - 1].focus();
        this.selectThumbByIndex(thumbIndex - 1);
        handled = true;
      } else if (event.key === 'ArrowRight' && thumbIndex < thumbCount - 1) {
        event.preventDefault();
        this.thumbImages[thumbIndex + 1].focus();
        this.selectThumbByIndex(thumbIndex + 1);
        handled = true;
      }
    } else {
      if (event.key === 'ArrowUp' && thumbIndex > 0) {
        event.preventDefault();
        this.thumbImages[thumbIndex - 1].focus();
        this.selectThumbByIndex(thumbIndex - 1);
        handled = true;
      } else if (event.key === 'ArrowDown' && thumbIndex < thumbCount - 1) {
        event.preventDefault();
        this.thumbImages[thumbIndex + 1].focus();
        this.selectThumbByIndex(thumbIndex + 1);
        handled = true;
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.navigateZoom(-1);
        handled = true;
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigateZoom(1);
        handled = true;
      }
    }

    if (!handled && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.selectThumbByIndex(thumbIndex);
    }
  }

  selectThumbByIndex(index) {
    const zoomedMedia = this.querySelector(
      `#zoom-images-container [data-zoom-index="${index}"]`
    );
    if (zoomedMedia) {
      zoomedMedia.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.setActiveThumbnail(index);
    }
  }

  updateButtons() {
    const galleryWidth = this.galleryImages.clientWidth;
    const maxScrollLeft = this.galleryImages.scrollWidth - galleryWidth;
    const imageWidth = this.images[0].clientWidth;
    const visibleImagesCount = Math.round(galleryWidth / imageWidth);

    this.prevBtn.disabled = this.galleryImages.scrollLeft <= 0;
    this.nextBtn.disabled = maxScrollLeft === this.galleryImages.scrollLeft;

    this.prevBtn.querySelector('.prev-btn__counter').innerText =
      `${Math.max(this.currentIndex, 1)}/${this.imageCount}`;

    this.nextBtn.querySelector('.next-btn__counter').innerText =
      `${Math.min(this.currentIndex + visibleImagesCount + 1, this.imageCount)}/${this.imageCount}`;
  }

  handleMouseEvents(event) {
    const imageCustomCursor = event.target.closest('.gallery-image')?.querySelector('.js-custom-cursor');
    const customCursor = event.currentTarget.querySelector('.js-custom-cursor');
    const cursor = imageCustomCursor ?? customCursor;

    if (event.type === 'mousemove') {
      if (imageCustomCursor) {
        customCursor.style.visibility = 'hidden';
      } else {
        customCursor.style.visibility = 'visible';
      }

      const rect = event.currentTarget.getBoundingClientRect();

      let offsetX = event.clientX - rect.left + event.currentTarget.scrollLeft;
      let offsetY = event.clientY - rect.top + event.currentTarget.scrollTop;

      if (imageCustomCursor) {
        offsetX = event.clientX;
      }

      cursor.style.top = `${offsetY}px`;
      cursor.style.left = `${offsetX}px`;
    }
  }

  updateDots() {
    const activeDot = this.dots[this.currentIndex];

    if (this.activeDot) {
      this.activeDot.classList.remove('tw-dot-active');
    }
    if (activeDot) {
      activeDot.classList.add('tw-dot-active');
      this.activeDot = activeDot;
    }
  }

  handleThumbsClick(event) {
    const clickedThumbIndex = event.currentTarget.dataset.thumbIndex;
    this.querySelector(
      `#zoom-images-container [data-zoom-index="${clickedThumbIndex}"]`
    ).scrollIntoView({ behavior: "smooth" });
  }

  setActiveThumbnail(index) {
    const newActiveThumb = this.querySelector(
      `#thumbnails-bar [data-thumb-index="${index}"]`
    );

    if (!newActiveThumb) return;

    if (!this.activeThumb) {
      newActiveThumb.classList.add('tw-thumbnail-active');
      this.activeThumb = newActiveThumb;
    }
    else if (this.activeThumb !== newActiveThumb) {
      this.activeThumb.classList.remove('tw-thumbnail-active');
      newActiveThumb.classList.add('tw-thumbnail-active');
      this.activeThumb = newActiveThumb;
    }
  }

  handleZoomScroll() {
    let visibleZoomImageIndex;

    if (this.isMobile) {
      visibleZoomImageIndex = Math.round(
        this.zoomImagesContainer.scrollLeft / this.zoomImages[0].clientWidth
      );
    } else {
      visibleZoomImageIndex = Math.round(
        this.zoomImagesContainer.scrollTop / this.zoomImages[0].clientHeight
      );
    }

    this.setActiveThumbnail(visibleZoomImageIndex);
  }

  openZoom(image, index, event) {
    if (index < 0 || index >= this.imageCount) return;

    this.lastFocusedElement = document.activeElement;

    this.documentBody.style.overflowY = "hidden";

    this.zoomOverlay.classList.remove('tw-hidden');
    this.zoomOverlay.setAttribute('aria-hidden', 'false');

    this.zoomClose.focus();

    this.setActiveThumbnail(index);

    const zoomedMedia = this.querySelector(
      `#zoom-images-container [data-zoom-index="${index}"]`
    );

    if (!zoomedMedia) return;

    if (event instanceof MouseEvent) {
      const rect = image.getBoundingClientRect();
      const topOffset = event.clientY - rect.top;
      const bottomOffset = rect.bottom - event.clientY;
      const height = rect.height;

      const topThreshold = height * 0.3;
      const bottomThreshold = height * 0.3;

      if (topOffset < topThreshold) {
        zoomedMedia.scrollIntoView({ behavior: 'instant', block: 'start' });
      } else if (bottomOffset < bottomThreshold) {
        zoomedMedia.scrollIntoView({ behavior: 'instant', block: 'end' });
      } else {
        zoomedMedia.scrollIntoView({ behavior: 'instant', block: 'center' });
      }
    } else {
      zoomedMedia.scrollIntoView({ behavior: 'instant', block: 'center' });
    }
  }

  closeZoom() {
    this.documentBody.style.overflowY = "auto";

    this.zoomOverlay.classList.add('tw-hidden');
    this.zoomOverlay.setAttribute('aria-hidden', 'true');

    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }
  }
}

customElements.define('ro-product-media-gallery', RoProductMediaGallery);
